import { type Badge, type BadgeChild, type BadgeVariant } from '../emailTemplateTypes'

type Json = Record<string, unknown>

const fail = (node: string, key: string): never => {
  throw new Error(`${node} needs ${key}`)
}

export const requireString = (json: Json, node: string, key: string): string => (typeof json[key] === 'string' ? (json[key] as string) : fail(node, key))

export const requireBoolean = (json: Json, node: string, key: string): boolean => (typeof json[key] === 'boolean' ? (json[key] as boolean) : fail(node, key))

export const requireNumber = (json: Json, node: string, key: string): number => (typeof json[key] === 'number' ? (json[key] as number) : fail(node, key))

export const requireArray = <T>(json: Json, node: string, key: string): T[] => (Array.isArray(json[key]) ? (json[key] as T[]) : fail(node, key))

export const requireObject = <T extends object>(json: Json, node: string, key: string): T =>
  typeof json[key] === 'object' && json[key] !== null && !Array.isArray(json[key]) ? (json[key] as T) : fail(node, key)

export const optionalString = (json: Json, node: string, key: string): string | undefined =>
  json[key] === undefined ? undefined : requireString(json, node, key)

const NAME = /^[a-z_][a-z0-9_]*$/

export const requireName = (json: Json, node: string, key: string): string => {
  const value = requireString(json, node, key)
  return NAME.test(value) ? value : fail(node, key)
}

const BADGE_VARIANTS: ReadonlySet<string> = new Set<BadgeVariant>(['secondary'])

const readBadgeChild = (child: unknown): BadgeChild | null => {
  if (typeof child !== 'object' || child === null) {
    return null
  }
  const { type, text, variable } = child as Json
  if (type === 'text' && typeof text === 'string' && text !== '') {
    return { type: 'text', text }
  }
  return type === 'variable' && typeof variable === 'string' && NAME.test(variable) ? { type: 'variable', variable } : null
}

const readBadge = (badge: unknown): Badge | null => {
  const raw = typeof badge === 'object' && badge !== null ? (badge as Json) : {}
  const children = Array.isArray(raw.children) ? raw.children.map(readBadgeChild) : []
  const valid = typeof raw.variant === 'string' && BADGE_VARIANTS.has(raw.variant) && children.length > 0 && children.every((child) => child !== null)
  return valid ? { variant: raw.variant as BadgeVariant, children: children as BadgeChild[] } : null
}

export const optionalBadges = (json: Json, node: string): Badge[] | undefined => {
  if (json.badges === undefined) {
    return undefined
  }
  const badges = Array.isArray(json.badges) ? json.badges.map(readBadge) : []
  return badges.length > 0 && badges.every((badge) => badge !== null) ? (badges as Badge[]) : fail(node, 'badges')
}
